import { chatDrawerActions } from 'ducks/chatDrawer'
import { isActionWhitelisted, requiresConfirmation } from 'layout/widgets/adaChat/actionPermissions'
import { resolveSystemTemplatesWithRestAPI } from 'layout/widgets/adaChat/utils/resolveSystemTemplates'
import {
  isInDesignTab,
  validateActionRequirementsOrThrow,
} from 'layout/widgets/adaChat/utils/validateActionRequirements'
import { LiveFunctionCall } from '../GeminiLiveAPI/lib/multimodal-live-types'

interface RunAutoDesignArgs {
  template_name?: string
  template_id?: number
  system_templates_json?: any[]
  confirmed?: boolean // Optional flag to indicate user has confirmed
}

export const TOOL_NAME = 'run_auto_design'

export async function RunAutoDesignToolCall(functionCall: LiveFunctionCall, history: any, orgId: number | undefined) {
  const args = functionCall.args as RunAutoDesignArgs

  console.log('RunAutoDesignToolCall', args)

  if (!orgId) {
    throw new Error('Organization ID is required to run auto design')
  }

  // Check if this action requires confirmation
  // In voice mode, Ada should ask for confirmation before calling this tool
  // If called without confirmation, we throw an error to prompt Ada to ask the user
  // However, if the action is whitelisted, skip the confirmation check
  const isWhitelisted = isActionWhitelisted('autoDesignEphemeral')

  if (requiresConfirmation('autoDesignEphemeral') && !isWhitelisted) {
    // Check if confirmation was explicitly provided in args
    if (!args.confirmed) {
      const errorMessage =
        'This action requires confirmation. Please ask the user: "Would you like me to run auto design?" and wait for their confirmation before proceeding.'
      console.error('[Voice] Auto design requires confirmation but confirmed flag not set:', errorMessage)
      throw new Error(errorMessage)
    }
  }

  const pathname = window.location.pathname
  validateActionRequirementsOrThrow('autoDesignEphemeral', pathname)

  // Extract project ID from pathname
  const { projectId } = isInDesignTab(pathname)

  // Resolve system templates using shared utility (uses REST API for voice mode)
  const systemTemplatesJson = await resolveSystemTemplatesWithRestAPI(args, orgId)

  // Run auto design with the template(s)
  // Note: autoDesignRunAndLoadWorker is a long-running background process
  // It will run auto-design and show results, waiting for user acceptance before loading
  if (systemTemplatesJson.length > 0) {
    // Create a message with toolExecutionState so the AutoDesignProgressItem shows
    if (window.reduxStore && projectId) {
      const state = window.reduxStore.getState()
      const messages = state?.chatDrawer?.messages || []
      const lastMessageIndex = messages.length > 0 ? messages.length - 1 : -1

      // If there's a previous message, attach toolExecutionState to it
      // Otherwise create a new message
      if (lastMessageIndex >= 0) {
        window.reduxStore.dispatch(
          chatDrawerActions.updateToolExecutionState(lastMessageIndex, {
            toolType: 'autoDesign',
            status: 'running',
            projectId: projectId,
            isAutoAccepted: isWhitelisted,
          })
        )
      } else {
        // Fallback: create a new message if there are no messages
        window.reduxStore.dispatch(chatDrawerActions.addMessage('Running auto-design...', 'receive', 'voice'))
        // Then add toolExecutionState to the newly created message
        const newState = window.reduxStore.getState()
        const newMessages = newState?.chatDrawer?.messages || []
        const newLastMessageIndex = newMessages.length > 0 ? newMessages.length - 1 : -1
        if (newLastMessageIndex >= 0) {
          window.reduxStore.dispatch(
            chatDrawerActions.updateToolExecutionState(newLastMessageIndex, {
              toolType: 'autoDesign',
              status: 'running',
              projectId: projectId,
              isAutoAccepted: isWhitelisted,
            })
          )
        }
      }
    }

    // Run auto-design using the worker (same as text mode)
    // This will show results and wait for user acceptance before loading
    window.AutoDesignHelper.autoDesignRunAndLoadWorker(
      null,
      systemTemplatesJson,
      null,
      null,
      null,
      {},
      false,
      null,
      null,
      null,
      false,
      true, // showNotifications
      {}
    )
      .then(async (response: any) => {
        console.log('[Voice] Auto design worker completed, response:', response)
        // The worker already handles async polling internally, so by the time we get here,
        // the polling should be complete and we have the final designData
        if (!response?.designData || !projectId) {
          console.warn('[Voice] Auto design response missing designData or required context', {
            hasResponse: !!response,
            hasDesignData: !!response?.designData,
            projectId,
          })
          return
        }

        const designData = response.designData
        const autoDesignResult = response.autoDesignResult
        console.log('[Voice] Processing design data, systems count:', designData?.object?.children?.length || 0)

        const systems = designData?.object?.children?.filter((child: any) => child.type === 'OsSystem') || []

        // Build description
        let description = ''
        systems.forEach((system: any) => {
          const kwStc = system.userData?.kw_stc || 0
          const batteryKwh = system.userData?.battery_total_kwh || 0
          if (description) description += ' '
          description += `${kwStc} kW PV`
          if (batteryKwh > 0) {
            description += `, ${batteryKwh} kWh Battery`
          }
        })

        // Update the message to show ready state (waiting for user acceptance)
        if (window.reduxStore) {
          const state = window.reduxStore.getState()
          const messages = state?.chatDrawer?.messages || []

          // Find the last receive message with toolExecutionState
          let targetMessageIndex = -1
          for (let i = messages.length - 1; i >= 0; i--) {
            if (
              messages[i].direction === 'receive' &&
              messages[i].toolExecutionState &&
              messages[i].toolExecutionState.toolType === 'autoDesign'
            ) {
              targetMessageIndex = i
              break
            }
          }

          if (targetMessageIndex >= 0) {
            const existingState = messages[targetMessageIndex].toolExecutionState
            window.reduxStore.dispatch(
              chatDrawerActions.updateToolExecutionState(targetMessageIndex, {
                toolType: 'autoDesign',
                status: 'ready',
                description: description || 'Auto-design complete',
                data: { designData, autoDesignResult },
                designData: designData, // Keep for backward compatibility
                projectId: projectId,
                isAutoAccepted: existingState?.isAutoAccepted,
                templateId: systemTemplatesJson.length === 1 ? systemTemplatesJson[0].id : undefined,
              })
            )
            console.log('[Voice] Updated auto design message to ready state')
          }
        }
      })
      .catch((error) => {
        console.error('[Voice] Auto design error:', error)

        // Update the toolExecutionState to 'error' so the UI shows the failure
        if (window.reduxStore && projectId) {
          const state = window.reduxStore.getState()
          const messages = state?.chatDrawer?.messages || []

          // Find the last receive message with auto-design toolExecutionState
          let targetMessageIndex = -1
          for (let i = messages.length - 1; i >= 0; i--) {
            if (
              messages[i].direction === 'receive' &&
              messages[i].toolExecutionState &&
              messages[i].toolExecutionState.toolType === 'autoDesign'
            ) {
              targetMessageIndex = i
              break
            }
          }

          if (targetMessageIndex >= 0) {
            // Extract error details from error object
            const errorData = error?.cause || error
            const errorMessage = errorData?.message || errorData?.detail || error?.message || 'Auto-design failed'
            const errorCode = errorData?.['error-code'] || errorData?.errorCode
            const errorDetails = errorData?.['error-details'] || errorData?.errorDetails

            window.reduxStore.dispatch(
              chatDrawerActions.updateToolExecutionState(targetMessageIndex, {
                toolType: 'autoDesign',
                status: 'error',
                projectId: projectId,
                description: errorMessage,
                error: {
                  message: errorMessage,
                  errorCode: errorCode,
                  errorDetails: errorDetails,
                  httpCode: errorData?.http_code,
                },
              })
            )
          }
        }
      })
  }

  // Return immediately - tool call state will be cleared by VoiceProvider
  // The auto-design process continues in the background
  return {
    success: true,
    message: `Auto design started with template${systemTemplatesJson.length > 1 ? 's' : ''}: ${systemTemplatesJson
      .map((t) => t.name || t.id)
      .join(', ')}`,
  }
}
